<template>
  <div :class="['screen', { 'screen-full': fullScreen }]">
    <aside
      v-if="left || leftTransparent || leftBigger"
      :class="[
        'screen__left',
        { 'screen__left--transparent': leftTransparent },
        { 'screen__left--bigger': leftBigger },
        { 'screen__left--auto-height': leftAutoHeight }
      ]"
    >
      <slot name="screen-left" />
    </aside>

    <section
      v-if="centerBig"
      class="screen__center--big"
    >
      <slot name="screen-center" />
    </section>

    <section
      v-else-if="center || centerTransparent || centerEspecial"
      :class="[
        'screen__center',
        { 'screen__center--transparent': centerTransparent },
        { 'screen__center--especial': centerEspecial }
      ]"
    >
      <slot name="screen-center" />
    </section>

    <aside
      v-if="right || rightTransparent"
      :class="[
        'screen__right',
        { 'screen__right screen__right--transparent': rightTransparent }
      ]"
    >
      <slot name="screen-right" />
    </aside>

    <Header
      class="screen__header"
      :title="headerTitle"
      :no-shadow="fullScreen"
    >
      <ElButton
        v-if="backButton"
        slot="header-left"
        class="button__back"
        type="text"
        @click="goToPreviousRoute"
      >
        <i class="el-icon-arrow-left" />
        {{ backButtonText || 'Go back' }}
      </ElButton>

      <!-- Forwards the content of the header Screen slot to the Header slots -->
      <slot
        slot="header-left"
        name="header-left"
      />

      <slot
        slot="header-center"
        name="header-center"
      />

      <slot
        slot="header-right"
        name="header-right"
      />

      <ElButton
        v-if="shouldDisplayFAQLink"
        slot="header-right"
        type="text"
        @click="openFAQInNewTab"
      >
        FAQ
      </ElButton>

      <ElButton
        v-if="presentationViewSwitch"
        slot="header-right"
        plain
        type="primary"
        @click="switchToPresentationView"
      >
        Present Boards
      </ElButton>

      <ElButton
        v-if="collectionOverviewSwitch && groupId && seasonId"
        slot="header-right"
        plain
        type="primary"
        @click="switchToCollectionOverview"
      >
        <i class="el-icon-s-grid" />
      </ElButton>

      <ElDropdown
        v-if="$route.name === 'Projects'"
        slot="header-right"
      >
        <ElButton>
          Settings<i class="el-icon-arrow-down el-icon--right" />
        </ElButton>
        <ElDropdownMenu slot="dropdown">
          <Feature
            :name="featureInviteManager"
            :options="{ user }"
          >
            <span
              v-if="isUserCreatorInGroup"
              @click="showInviteManager = true"
            ><ElDropdownItem>Manage People</ElDropdownItem></span>
          </Feature>
          <span
            @click="logout"
          ><ElDropdownItem divided>Logout</ElDropdownItem></span>
        </ElDropdownMenu>
      </ElDropdown>
    </Header>

    <StitchDialog
      v-if="groupId"
      title="Manage People"
      :center="true"
      :visible="showInviteManager"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      custom-class="header__invite-manager-dialog"
    >
      <InviteManager
        class="header__invite-manager"
        :group-id="groupId"
        @close-invite-manager="showInviteManager = false"
      />
    </StitchDialog>
  </div>
</template>

<script>
import Header from '@/components/Header.vue'
import InviteManager from '@/components/project/InviteManager'
import Utils from '@/services/utils'

export default {
  name: 'Screen',

  components: {
    Header,
    InviteManager
  },

  props: {
    left: {
      type: Boolean,
      default: false
    },
    leftTransparent: {
      type: Boolean,
      default: false
    },
    leftBigger: {
      type: Boolean,
      default: false
    },
    leftAutoHeight: {
      type: Boolean,
      default: false
    },
    center: {
      type: Boolean,
      default: false
    },
    centerEspecial: {
      type: Boolean,
      default: false
    },
    centerBig: {
      type: Boolean,
      default: false
    },
    centerTransparent: {
      type: Boolean,
      default: false
    },
    right: {
      type: Boolean,
      default: false
    },
    rightTransparent: {
      type: Boolean,
      default: false
    },
    headerTitle: {
      type: String,
      default: 'Digital Foamboard'
    },
    backButton: {
      type: Boolean,
      default: false
    },
    backButtonText: {
      type: String,
      default: null
    },
    backButtonRoute: {
      type: String,
      default: null
    },
    presentationViewSwitch: {
      type: Boolean,
      default: false
    },
    collectionOverviewSwitch: {
      type: Boolean,
      default: false
    },
    fullScreen: {
      type: Boolean,
      default: false
    },
    groupId: {
      type: Number,
      default: null
    },
    seasonId: {
      type: Number,
      default: null
    },
    user: {
      type: Object,
      default: null
    }
  },

  data: () => ({
    projectId: null,
    showInviteManager: false,
    featureInviteManager: 'invite-manager',
    faqReadOnlyUrl: require('../assets/documents/Faq_Digital-Foamboard_Read-Only.pdf')
  }),

  computed: {
    /**
     * @returns {boolean}
     */
    isUserCreatorInGroup () {
      return Utils.isUserCreatorInGroup(this.user, this.groupId)
    },

    /**
     * @returns {string}
     */
    currentRouteName () {
      return this.$route.name
    },

    /**
     * @returns {boolean}
     */
    shouldDisplayFAQLink () {
      if (['Projects', 'AllBoards'].includes(this.currentRouteName)) {
        return !this.isUserCreatorInGroup
      } else {
        return false
      }
    },

    /**
     * @returns {boolean}
     */
    shouldDisplayIntercom () {
      if (this.isUserCreatorInGroup) {
        return !['Collection', 'Present'].includes(this.currentRouteName)
      } else {
        return true
      }
    }
  },

  created () {
    this.projectId = parseInt(this.$route.params.id)
    this.updateIntercomVisibility(this.shouldDisplayIntercom)
  },

  methods: {
    /**
     * @param {boolean} shouldDisplayIntercom
     */
    updateIntercomVisibility (shouldDisplayIntercom) {
      if (shouldDisplayIntercom) {
        document.querySelector('body').classList.remove('intercom--hidden')
      } else {
        document.querySelector('body').classList.add('intercom--hidden')
      }
    },

    /**
     */
    goToPreviousRoute () {
      if (this.backButtonRoute) {
        this.$router.push(this.backButtonRoute)
      } else {
        this.$router.push('/')
      }
    },

    /**
     */
    openFAQInNewTab () {
      window.open(this.faqReadOnlyUrl, '_blank')
    },

    /**
     */
    switchToPresentationView () {
      this.$router.push(`/projects/${this.projectId}/present`)
    },

    /**
     */
    switchToCollectionOverview () {
      this.$router.push(
        `/collection?group_id=${this.groupId}&season_id=${this.seasonId}`
      )
    },

    /**
     */
    logout () {
      const logoutUrl = new URL(`${this.$boardsApiBaseUrl}id_auth/logout/`)

      logoutUrl.searchParams.set(
        'state',
        `${window.location.origin}${this.$router.options.base}`
      )

      window.location.assign(logoutUrl.href)
    }
  }
}
</script>

<style lang="scss" scoped>
$background-color-screen-overlay: rgba(255, 255, 255, 0.4);
$z-index-screen-overlay: -1;

$height-screen-center-big: calc(100% - #{$height-header});
$max-width-invite-manager: 600px;
$max-height-screen-left-auto-height: calc(
  100% - #{$height-header + $gutter * 2}
);
$width-screen-left-bigger: 20%;
$width-screen-center-big: calc(90% - #{$gutter});
$width-screen-center-especial: calc(100% - 388px);
$screen-left-top-margin: $height-header + $gutter;

.screen {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100vh;
  padding: 0 $gutter;
  overflow: hidden;

  &::before,
  &::after {
    @include size(100%);

    position: absolute;
    left: 0;
    z-index: $z-index-screen-overlay;
    background-color: $background-color-screen-overlay;
    background-size: cover;
    content: '';
  }

  &::before {
    filter: blur(10px);
  }
}

.screen__header {
  position: absolute;
  top: 0;
  left: 0;
  padding: 0 $gutter;
}

.screen__left,
.screen__center,
.screen__right {
  @include size(100%, calc(100% - #{$height-header + $gutter * 2}));

  position: relative;
  display: flex;
  flex-direction: column;
  margin-top: $height-header;
  padding: $spacer-unit-x2;
  background-color: $white;
  border-radius: $border-radius-default;

  &--transparent {
    background-color: transparent;
  }
}

.screen__right {
  max-width: $max-width-aside-medium;
  margin-left: $gutter;
}

.screen__left {
  max-width: $max-width-aside-medium;
  margin-right: $gutter;
}

.screen__center {
  flex: 1;
  width: 100%;
  padding: 0;

  &.screen__center--especial {
    width: $width-screen-center-especial;
  }
}

.screen-full {
  background-color: $white;

  .screen__left,
  .screen__center,
  .screen__right {
    margin-top: 0;
  }
}

.screen__left--bigger {
  width: $width-screen-left-bigger;
  max-width: $max-width-aside-large;
}

.screen__left--auto-height {
  height: auto;
  min-height: $screen-left-min-height;
  max-height: $max-height-screen-left-auto-height;
  margin-top: $screen-left-top-margin;
  margin-bottom: auto;
}

.screen__center--big {
  @include scroll-y-smooth();

  display: block;
  width: $width-screen-center-big;
  height: $height-screen-center-big;
  margin-top: $height-header;
}

.header__button--preview {
  padding: $spacer-unit-small $spacer-unit-x2 $spacer-unit-small $spacer-unit;
  background-color: $grey-blue-light;
}

.header__logout {
  color: $blue;
  cursor: pointer;
}

.header__invite-manager {
  max-width: $max-width-invite-manager;
  margin: 0 auto;
}
</style>

<style lang="scss">
.intercom--hidden {
  #intercom-container {
    display: none;
  }
}
</style>
